import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Input } from "@/components/ui/input";
import { Search, LogOut, MapPin, Clock } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/activity")({
  head: () => ({ meta: [{ title: "Hareketler — JUËN" }] }),
  component: ActivityPage,
});

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface Visit {
  id: string;
  path: string;
  entered_at: string;
  left_at: string | null;
  duration_seconds: number | null;
  event_type: string;
}

function formatDuration(s: number | null) {
  if (s == null) return "—";
  if (s < 60) return `${s} sn`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m} dk` : `${m} dk ${r} sn`;
}

function ActivityPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    void supabase
      .from("profiles")
      .select("id, first_name, last_name, email")
      .order("first_name")
      .then(({ data, error }) => {
        if (error) toast.error(error.message);
        else setMembers((data as Member[]) ?? []);
      });
  }, [isAdmin]);

  useEffect(() => {
    if (!selectedId) {
      setVisits([]);
      return;
    }
    setLoadingVisits(true);
    void supabase
      .from("page_visits")
      .select("*")
      .eq("user_id", selectedId)
      .order("entered_at", { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        if (error) toast.error(error.message);
        else setVisits((data as Visit[]) ?? []);
        setLoadingVisits(false);
      });
  }, [selectedId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return members;
    const q = query.toLowerCase();
    return members.filter(
      (m) =>
        m.first_name.toLowerCase().includes(q) ||
        m.last_name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q),
    );
  }, [members, query]);

  const selected = members.find((m) => m.id === selectedId);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Üye Hareketleri</h1>
          <p className="mt-2 text-muted-foreground">Bir üye seç, sayfa ziyaretlerini ve süreleri gör.</p>
        </div>

        <AdminTabs />

        <div className="grid gap-6 md:grid-cols-[280px_1fr]">
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Üye ara..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-card shadow-card">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">Üye yok</div>
              ) : (
                filtered.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    className={`block w-full border-b border-border px-4 py-3 text-left text-sm transition-colors last:border-0 hover:bg-muted ${
                      selectedId === m.id ? "bg-muted font-medium" : ""
                    }`}
                  >
                    <div>{m.first_name} {m.last_name}</div>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            {!selected ? (
              <div className="py-16 text-center text-muted-foreground">Soldan bir üye seç</div>
            ) : loadingVisits ? (
              <div className="py-16 text-center text-muted-foreground">Yükleniyor...</div>
            ) : visits.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">Henüz hareket yok</div>
            ) : (
              <div className="space-y-2">
                <h2 className="mb-3 text-lg font-semibold">
                  {selected.first_name} {selected.last_name} — son {visits.length} hareket
                </h2>
                {visits.map((v) => {
                  const isLogout = v.event_type === "logout";
                  return (
                    <div
                      key={v.id}
                      className={`flex items-start gap-3 rounded-lg border border-border p-3 ${
                        isLogout ? "bg-destructive/5" : "bg-background"
                      }`}
                    >
                      <div className="mt-0.5">
                        {isLogout ? (
                          <LogOut className="h-4 w-4 text-destructive" />
                        ) : (
                          <MapPin className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm break-all">
                          {isLogout ? "Çıkış yaptı" : v.path}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>{new Date(v.entered_at).toLocaleString("tr-TR")}</span>
                          {!isLogout && (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDuration(v.duration_seconds)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
